import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { DataCacheProvider } from './contexts/DataCacheContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import PageTransition from './components/PageTransition';
import Landing from './components/Landing';
import Auth from './components/Auth';
import Home from './components/Home';
import LearningPath from './components/LearningPath';
import InteractiveLesson from './components/InteractiveLesson';
import LessonComplete from './components/LessonComplete';
import BossBattle from './components/BossBattle';
import Dashboard from './components/Dashboard';
import Analytics from './components/Analytics';
import Profile from './components/Profile';
import StudyGuide from './components/StudyGuide';
import Quiz from './components/Quiz';
import FlashcardsEnhanced from './components/FlashcardsEnhanced';
import FlashcardTopics from './components/FlashcardTopics';
import FlashcardDecks from './components/FlashcardDecks';
import Progress from './components/Progress';
import AppShell from './components/AppShell';
import WorldMap from './components/worldmap/WorldMap';
import GameEngine from './components/games/GameEngine';
import Legal from './components/Legal';
import Pricing from './components/Pricing';
import FeedbackButton from './components/FeedbackButton';
// Admin Components
import AdminRoute from './components/admin/AdminRoute';
import AdminDashboard from './components/admin/AdminDashboard';
import UserManagement from './components/admin/UserManagement';
import ContentManagement from './components/admin/ContentManagement';
import TopicManagement from './components/admin/TopicManagement';
import AdminAnalytics from './components/admin/AdminAnalytics';
import ActivityLogs from './components/admin/ActivityLogs';

// Animated Routes wrapper component
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Landing /></PageTransition>} />
        <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
        <Route path="/legal/:page" element={<PageTransition><Legal /></PageTransition>} />
        <Route path="/pricing" element={<PageTransition><Pricing /></PageTransition>} />
        <Route path="/home" element={
          <ProtectedRoute>
            <PageTransition><Home /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/learning-path" element={
          <ProtectedRoute>
            <PageTransition><LearningPath /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/interactive-lesson" element={
          <ProtectedRoute>
            <PageTransition><InteractiveLesson /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/lesson-complete" element={
          <ProtectedRoute>
            <PageTransition><LessonComplete /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/boss-battle" element={
          <ProtectedRoute>
            <PageTransition><BossBattle /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <PageTransition><Dashboard /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute>
            <PageTransition><Analytics /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <PageTransition><Profile /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/study-guide" element={
          <ProtectedRoute>
            <PageTransition><StudyGuide /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/quiz" element={
          <ProtectedRoute>
            <PageTransition><Quiz /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/flashcards" element={
          <ProtectedRoute>
            <PageTransition><FlashcardTopics /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/flashcards-study" element={
          <ProtectedRoute>
            <PageTransition><FlashcardsEnhanced /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/flashcard-decks" element={
          <ProtectedRoute>
            <PageTransition><FlashcardDecks /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/progress" element={
          <ProtectedRoute>
            <PageTransition><Progress /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/world-map" element={
          <ProtectedRoute>
            <PageTransition><WorldMap /></PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/game" element={
          <ProtectedRoute>
            <PageTransition><GameEngine /></PageTransition>
          </ProtectedRoute>
        } />
        {/* Admin Routes */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } />
        <Route path="/admin/users" element={
          <AdminRoute>
            <UserManagement />
          </AdminRoute>
        } />
        <Route path="/admin/content" element={
          <AdminRoute>
            <ContentManagement />
          </AdminRoute>
        } />
        <Route path="/admin/topics" element={
          <AdminRoute>
            <TopicManagement />
          </AdminRoute>
        } />
        <Route path="/admin/analytics" element={
          <AdminRoute>
            <AdminAnalytics />
          </AdminRoute>
        } />
        <Route path="/admin/logs" element={
          <AdminRoute>
            <ActivityLogs />
          </AdminRoute>
        } />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <DataCacheProvider>
            <ToastProvider>
              <AppShell>
                <AnimatedRoutes />
                <FeedbackButton />
              </AppShell>
            </ToastProvider>
          </DataCacheProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
